<template>
  <v-dialog
    :value="value"
    hide-overlay
    persistent
    :width="isMini ? miniWidth : 800">
    <v-card class="card">
      <v-toolbar flat class="accent" dark>
        <v-toolbar-title class="font-weight-light">New Item</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$emit('mini', (isMini = !isMini))">
          <v-icon>{{
            isMini ? 'mdi-window-maximize' : 'mdi-window-minimize'
          }}</v-icon>
        </v-btn>
        <v-btn icon @click="$emit('update:value', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container v-if="!isMini" class="pt-6 pb-0 px-6" fluid>
        <v-form ref="form">
          <v-row dense style="height: 60px">
            <v-col lg="4" xl="4">
              <v-text-field
                :rules="[v => !!v || 'UPC Required']"
                :class="body"
                outlined
                dense
                background-color="input"
                label="UPC"
                placeholder="Enter UPC"
                type="number"
                v-model="object.upc" />
            </v-col>
            <v-col lg="8" xl="8">
              <v-text-field
                :rules="[v => !!v || 'Description Required']"
                :class="body"
                outlined
                dense
                background-color="input"
                label="Description"
                placeholder="Enter Description"
                v-model="object.description" />
            </v-col>
          </v-row>
          <v-row dense align="center">
            <v-col>
              <v-text-field
                :rules="caseCostRules"
                :class="body"
                outlined
                dense
                background-color="input"
                type="number"
                prefix="$"
                label="Case Cost"
                v-model="object.case_cost" />
            </v-col>
            <v-col>
              <v-text-field
                :rules="packRules"
                :class="body"
                outlined
                dense
                background-color="input"
                type="number"
                label="Pack"
                v-model="object.pack" />
            </v-col>
            <v-icon large class="mb-6">mdi-slash-forward</v-icon>
            <v-col class="pr-0" style="border-right-top-radius: 0px">
              <v-text-field
                :rules="sizeRules"
                style="
                  border-top-right-radius: 0px;
                  border-bottom-right-radius: 0px;
                "
                :class="body"
                outlined
                dense
                label="Size"
                background-color="input"
                type="number"
                v-model="object.size" />
            </v-col>
            <v-col class="pl-0">
              <v-autocomplete
                auto-select-first
                :rules="[v => !!v || 'Measurement Required']"
                style="
                  border-top-left-radius: 0px;
                  border-bottom-left-radius: 0px;
                "
                :class="body"
                outlined
                dense
                background-color="input"
                :items="measurements"
                label="Unit of Measure"
                v-model="object.measure_unit"
                :menu-props="{ maxHeight: 130 }" />
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-divider v-if="!isMini" />
      <v-toolbar v-if="!isMini" flat class="card">
        <v-spacer />
        <v-btn
          :class="body"
          class="primary white--text"
          rounded
          @click="addNewItem">
          <v-icon left>mdi-plus</v-icon>Add
        </v-btn>
      </v-toolbar>
      <v-overlay absolute v-model="saving">
        <v-progress-circular indeterminate size="100">
          Saving...
        </v-progress-circular>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import { displayHelpers } from '@/mixins/display-helpers'
import { mapState } from 'vuex'

export default {
  name: 'ItemNew',

  props: {
    value: Boolean,
    contract: Object,
    items: Array,
    saving: Boolean,
    isMini: Boolean,
    miniWidth: Number
  },

  mixins: [displayHelpers],

  data: () => ({
    object: {}
  }),

  computed: {
    ...mapState(['allStores']),

    measurements() {
      return ['CT', 'EACH', 'GR', 'INCH', 'LB', 'LITER', 'ML', 'PK', 'OZ']
    },

    caseCostRules() {
      const rules = []

      const valueRequired = v => !!v || 'Case Cost Required'
      rules.push(valueRequired)

      const positiveValue = v => parseFloat(v) > 0 || 'Invalid Amount'
      rules.push(positiveValue)

      return rules
    },

    packRules() {
      const rules = []

      const valueRequired = v => !!v || 'Pack Required'
      rules.push(valueRequired)

      const positiveValue = v => parseInt(v) > 0 || 'Invalid Amount'
      rules.push(positiveValue)

      return rules
    },

    sizeRules() {
      const rules = []

      const valueRequired = v => !!v || 'Size Required'
      rules.push(valueRequired)

      const positiveValue = v => parseFloat(v) > 0 || 'Invalid Amount'
      rules.push(positiveValue)

      return rules
    },

    itemKeys() {
      return [
        { label: 'Case Cost', key: 'case_cost', value: this.object.case_cost },
        {
          label: 'Off Invoice Amount',
          key: 'off_invoice_amount',
          value: this.object.off_invoice_amount
        },
        { label: 'ePay Ad', key: 'epay_ad', value: this.object.epay_ad },
        { label: 'ePay TPR', key: 'epay_tpr', value: this.object.epay_tpr },
        { label: 'Ad Scan', key: 'scan_ad', value: this.object.scan_ad },
        { label: 'TPR Scan', key: 'scan_tpr', value: this.object.scan_tpr },
        {
          label: 'Ad BillBack',
          key: 'billback_ad',
          value: this.object.billback_ad
        },
        {
          label: 'TPR Billback',
          key: 'billback_tpr',
          value: this.object.billback_tpr
        }
      ]
    }
  },

  methods: {
    getRows() {
      const numCols = 8
      const rows = this.itemKeys.reduce((acc, curr, index) => {
        const i = index
        const row = Math.floor(i / numCols)
        const mod = i % numCols
        if (mod === 0) {
          acc.push([])
        }
        acc[row].push(curr)
        return acc
      }, [])
      return rows
    },

    formatCurrency(key, value) {
      this.object[key] = ((value * 100) / 100).toFixed(2)
      this.$forceUpdate()
    },

    async addNewItem() {
      const validForm = this.$refs.form.validate()
      const validUPC =
        Object.keys(this.object).length > 0 &&
        this.object?.upc.length >= 10 &&
        this.object?.upc.length <= 13
      const itemExists = this.items.some(
        item => this.validUPC && item.upc === this.object.upc.padStart(13, '0')
      )
      if (validForm) {
        if (validUPC) {
          if (!itemExists) {
            const variances = await this.allStores.reduce(
              (acc, storeVariance) => {
                storeVariance = {
                  ad_conversion: null,
                  ad_multiple: '1',
                  ad_off_invoice_amount: '0.00',
                  ad_retail_percentage: '0.0',
                  ad_retail: '0.00',
                  billback_ad: '0.00',
                  billback_tpr: '0.00',
                  cost_change: false,
                  cost_change_value: '0.00',
                  case_cost: this.object.case_cost,
                  epay_ad: '0.00',
                  epay_tpr: '0.00',
                  included_flag: true,
                  kvi_group_name: '',
                  net_ad_unit_cost: '0.00',
                  net_tpr_unit_cost: '0.00',
                  pre_priced_value: '0.00',
                  pre_priced: false,
                  price_zone: storeVariance.PriceZone,
                  pricing_rules: false,
                  regular_conversion: null,
                  regular_retail_percentage: '0.0',
                  regular_retail: '0.00',
                  scan_ad: '0.00',
                  scan_tpr: '0.00',
                  store_number: storeVariance.StoreNumber,
                  tpr_conversion: null,
                  tpr_multiple: '1',
                  tpr_off_invoice_amount: '0.00',
                  tpr_retail_percentage: '0.0',
                  tpr_retail: '0.00',
                  unit_cost: '0.00',
                  upc: this.object.upc,
                  variance_uuid: this.$uuid.v4()
                }
                acc.push(storeVariance)
                return acc
              },
              []
            )

            const item = {
              description: this.object.description,
              item_exists: false,
              item_uuid: await this.$uuid.v4(),
              pack: this.object.pack,
              plg_ap_number: '',
              ppu_code: '',
              ppu_name: '',
              size: `${this.object.size} ${this.object.measure_unit}`,
              upc: this.object.upc.padStart(13, '0'),
              variances: variances
            }
            this.$emit('add', {
              newItem: true,
              data: item
            })
            this.object = {}
            this.$refs.form.resetValidation()
          } else {
            this.$store.dispatch('setSnackbar', {
              status: 'error',
              text: 'UPC Already Exists'
            })
          }
        } else {
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Invalid UPC Length'
          })
        }
      } else {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Required Fields Missing'
        })
      }
    }
  }
}
</script>
